// Status in Worten und als Farbklasse der Status-Pille - ein Ort für alle Seiten. Die Tabellen sind
// gegen die Unions in typen.ts typisiert: kommt im Backend ein Status dazu, meldet es der Typecheck hier.

import type {
  AnliegenStatus,
  Bestellstatus,
  EntscheidungArt,
  Importstatus,
  Inventurstatus,
  MailStatus,
  NotierungLaufQuelle,
  NotierungLaufStatus,
  NotierungPosition,
  NotierungRegel,
  NotierungRegelHerkunft,
  NotierungSignal,
  NotierungStatus,
  OffenerFaden,
  Rueckfragestatus,
  Schwere,
  Staffel,
  Vorgabe,
  Vorschlagstatus,
  WaechterArt,
  WerkzeugAnzeige,
} from './typen'
import { menge, stueck } from './format'

/** Die Farbklassen, die styles.css für `.status` kennt. */
export type Pille = 'offen' | 'gebucht' | 'gesperrt' | 'fehler' | 'verworfen' | 'beantwortet' | 'erledigt'

export interface StatusText {
  text: string
  klasse: Pille
}

export const IMPORTSTATUS: Record<Importstatus, StatusText> = {
  offen: { text: 'wartet auf Freigabe', klasse: 'offen' },
  gesperrt: { text: 'Kontrolle nicht bestanden', klasse: 'gesperrt' },
  gebucht: { text: 'gebucht', klasse: 'gebucht' },
  verworfen: { text: 'verworfen', klasse: 'verworfen' },
  fehler: { text: 'Fehler', klasse: 'fehler' },
}

export const INVENTURSTATUS: Record<Inventurstatus, StatusText> = {
  offen: { text: 'offen', klasse: 'offen' },
  zur_freigabe: { text: 'wartet auf Freigabe', klasse: 'offen' },
  gesperrt: { text: 'Kontrolle nicht bestanden', klasse: 'gesperrt' },
  abgeschlossen: { text: 'abgeschlossen', klasse: 'gebucht' },
}

export const MAILSTATUS: Record<MailStatus, StatusText> = {
  verarbeitet: { text: 'verarbeitet', klasse: 'gebucht' },
  abgewiesen: { text: 'abgewiesen', klasse: 'gesperrt' },
  ohne_inhalt: { text: 'nichts Lesbares', klasse: 'verworfen' },
  fehler: { text: 'Fehler', klasse: 'fehler' },
  zuordnen: { text: 'Bereich unklar', klasse: 'offen' },
}

export const VORSCHLAGSTATUS: Record<Vorschlagstatus, StatusText> = {
  entwurf: { text: 'Entwurf', klasse: 'offen' },
  entschieden: { text: 'entschieden', klasse: 'gebucht' },
  verworfen: { text: 'verworfen', klasse: 'gesperrt' },
}

export const RUECKFRAGESTATUS: Record<Rueckfragestatus, StatusText> = {
  offen: { text: 'wartet auf Antwort', klasse: 'offen' },
  beantwortet: { text: 'Vorschlag liegt vor', klasse: 'beantwortet' },
  erledigt: { text: 'erledigt', klasse: 'erledigt' },
  verworfen: { text: 'offen gelassen', klasse: 'verworfen' },
  // Beim Buchen ohne Antwort geschlossen (02.11.2026) - nicht offen, aber eine Antwort lässt sich nachtragen.
  uebergangen: { text: 'beim Buchen übergangen', klasse: 'verworfen' },
}

export const WERKZEUGZUSTAND: Record<WerkzeugAnzeige['zustand'], StatusText> = {
  laeuft: { text: 'läuft', klasse: 'offen' },
  fertig: { text: 'erledigt', klasse: 'gebucht' },
  fehler: { text: 'fehlgeschlagen', klasse: 'gesperrt' },
  wartet: { text: 'wartet auf Freigabe', klasse: 'beantwortet' },
  abgelehnt: { text: 'abgelehnt', klasse: 'verworfen' },
  // Nie gelaufen: danach ging das Gespräch weiter oder brach ab (02.11.2026).
  abgebrochen: { text: 'abgebrochen - nicht ausgeführt', klasse: 'verworfen' },
}

export const BESTELLSTATUS: Record<Bestellstatus, string> = {
  geplant: 'geplant',
  in_produktion: 'in Produktion',
  beim_hersteller: 'beim Hersteller',
  beauftragt: 'beauftragt',
  geliefert: 'geliefert',
}

export const SCHWERETEXT: Record<Schwere, string> = {
  fehler: 'sperrt den Import',
  warnung: 'bitte ansehen',
  hinweis: 'zur Kenntnis',
}

/** Die Schwere als Wort - die Datenprüfung setzt sie als Statuspille neben den Befund. */
export const SCHWERE: Record<Schwere, string> = {
  fehler: 'Fehler',
  warnung: 'Warnung',
  hinweis: 'Hinweis',
}

/** Die Arten des Entscheidungsgedächtnisses in Worten - Excel-Plan und Einstellungen zeigen dieselben. */
export const ENTSCHEIDUNGART: Record<EntscheidungArt, string> = {
  blattart: 'Blattart',
  gruppe: 'Gruppe',
  aktiv: 'Laden ja/nein',
  kopplung: 'Kopplung',
}

// --- Was der Assistent sich merkt (02.11.2026) ---

/** Wofür eine Vorgabe gilt - als Auswahl im Formular und als Wort in der Liste. */
export const VORGABEGELTUNG: Record<Vorgabe['geltung'], string> = {
  allgemein: 'allgemein',
  artikel: 'für Artikel',
  lieferant: 'für einen Lieferanten',
}

/** Was ein Wächter beobachtet. */
export const WAECHTERART: Record<WaechterArt, string> = {
  produktion_geplant: 'Produktion geplant',
  bestand_unter: 'Bestand niedrig',
  termin: 'Termin',
}

/** Was in einem Gespräch liegen geblieben ist. */
export const FADENART: Record<OffenerFaden['art'], string> = {
  frage: 'Frage offen',
  freigabe: 'Freigabe wartet',
  unbeantwortet: 'ohne Antwort',
  abbruch: 'abgebrochen',
  abruf: 'Abruf',
  import: 'Import',
}

export const ANLIEGENSTATUS: Record<AnliegenStatus, StatusText> = {
  offen: { text: 'offen', klasse: 'offen' },
  erledigt: { text: 'erledigt', klasse: 'erledigt' },
  abgelehnt: { text: 'abgelehnt', klasse: 'verworfen' },
}

/** Woher ein Journaleintrag kommt - die Schlüssel kommen aus dem Backend. */
export const QUELLE: Record<string, string> = {
  assistent: 'Assistent',
  dashboard: 'Oberfläche',
  postfach: 'Postfach',
  api: 'API',
}

// --- Rohstoffnotierungen (06.10.2026) ---

export const NOTIERUNG_LAUFSTATUS: Record<NotierungLaufStatus, StatusText> = {
  pruefen: { text: 'zu prüfen', klasse: 'offen' },
  uebernommen: { text: 'übernommen', klasse: 'gebucht' },
  verworfen: { text: 'verworfen', klasse: 'verworfen' },
  zurueckgenommen: { text: 'zurückgenommen', klasse: 'verworfen' },
  fehler: { text: 'nicht lesbar', klasse: 'fehler' },
}

/** Woher ein Lauf kam. */
export const NOTIERUNG_LAUFQUELLE: Record<NotierungLaufQuelle, string> = {
  mail: 'Mail',
  upload: 'hochgeladen',
  hand: 'von Hand',
  excel: 'Excel-Mappe',
  text: 'eingefügter Text',
}

/** Ein Preis, der keiner ist: „sold out" und „on request" stehen in Worten in der Zelle. */
export const NOTIERUNGSTATUS: Record<NotierungStatus, string> = {
  preis: 'Preis',
  ausverkauft: 'ausverkauft',
  auf_anfrage: 'auf Anfrage',
}

/** Das Signal in Worten - in der Tabelle steht das Wort an der Zelle, nicht nur die Farbe. */
export const SIGNALTEXT: Record<NotierungSignal, { kurz: string; lang: string }> = {
  stark: { kurz: 'stark', lang: 'starker Rückgang an einem Tag - Einkaufstag' },
  signal: { kurz: 'Signal', lang: 'deutlicher Rückgang zum Vortag' },
  trend: { kurz: 'Trend', lang: 'fällt seit mehreren Notierungstagen' },
  anstieg: { kurz: 'Anstieg', lang: 'deutlicher Anstieg zum Vortag' },
}

/** Woher eine Zuordnungsregel kommt - in Worten in der Liste der Regeln. */
export const NOTIERUNG_REGELHERKUNFT: Record<NotierungRegelHerkunft, string> = {
  startdaten: 'aus den Startdaten',
  spalte: 'aus der Spalte',
  gelernt: 'gelernt aus einem Lauf',
  hand: 'von Hand',
  assistent: 'vom Assistenten',
}

/** „Ladeort enthält Rotterdam · Qualität SG“ - die Bedingungen einer Regel außer dem Produkt; leer: keine. */
export function regelBedingungen(r: Pick<NotierungRegel, 'ladeort' | 'qualitaet'>): string {
  return [r.ladeort && `Ladeort enthält ${r.ladeort}`, r.qualitaet && `Qualität ${r.qualitaet}`].filter(Boolean).join(' · ')
}

/** Die ganze Regel in einem Satz: „Cargill: „RBD PO“, Ladeort enthält Rotterdam → Spalte … (raff. Palmöl)“. */
export function regelInWorten(r: Pick<NotierungRegel, 'lieferant' | 'produkt' | 'ladeort' | 'qualitaet' | 'aktion' | 'quelle' | 'rohstoff'>): string {
  const bedingungen = regelBedingungen(r)
  const wirkung = r.aktion === 'ignorieren' ? 'nicht übernehmen' : `Spalte ${r.quelle ?? '?'}${r.rohstoff ? ` (${r.rohstoff})` : ''}`
  return `${r.lieferant}: „${r.produkt}“${bedingungen ? `, ${bedingungen}` : ''} → ${wirkung}`
}

/** Wer eine Position einer Spalte zugeordnet hat. */
export const ZUORDNUNGTEXT: Record<NonNullable<NotierungPosition['zuordnung']>, string> = {
  regel: 'nach Regel',
  ki: 'von der KI',
  hand: 'von Hand',
}

/** Die Schwelle einer Preisstufe in Worten: „ab 20 Pal.“ oder „ab 50.000 Stück“ - genau eine ist gesetzt. */
export function staffelSchwelle(s: Pick<Staffel, 'ab_stueck' | 'ab_paletten'>): string {
  if (s.ab_paletten !== null && s.ab_paletten !== undefined) {
    return `ab ${Number.isInteger(s.ab_paletten) ? s.ab_paletten : menge(s.ab_paletten)} Pal.`
  }
  return `ab ${stueck(s.ab_stueck)} Stück`
}
